import {Button, Combobox, createListCollection, Drawer, Field, HStack, Icon, Input, Portal, Select, Separator, Spinner, Switch, Text, Textarea, VStack} from "@chakra-ui/react"
import {IoCloseSharp} from "react-icons/io5";
import {HiOutlinePlus, HiOutlineTrash} from "react-icons/hi2";
import {HiCheck, HiX} from "react-icons/hi";
import {LuChevronDown} from "react-icons/lu";
import {useState} from "react";
import useEndpointForm from "@/components/hooks/useEndpointForm";
import {showToast} from "@/components/ui/toaster";

const methodCollection = createListCollection({
    items: [
        {label: "GET", value: "Get"},
        {label: "POST", value: "Post"},
        {label: "PUT", value: "Put"},
        {label: "DELETE", value: "Delete"},
    ]
})

const bodyTypeCollection = createListCollection({
    items: [
        {label: "string", value: "string"},
        {label: "number", value: "number"},
        {label: "boolean", value: "boolean"},
        {label: "object", value: "object"},
        {label: "array", value: "array"},
    ]
})

const BodySchemaNode = ({node, depth = 0, addChildBodyNode, removeBodyNode, updateBodyNode, isArrayItem = false}) => {
    const isComplex = node.type === "object" || node.type === "array"
    return (
        <VStack w={'full'} gap={2} alignItems={'flex-start'} pl={depth * 4}>
            <HStack w={'full'} gap={2} alignItems={'center'}>
                {!isArrayItem && (
                    <Input
                        variant={'primary'}
                        placeholder="Field name"
                        value={node.key}
                        onChange={(e) => updateBodyNode(node.id, 'key', e.target.value)}
                        flex={1}
                    />
                )}
                <Select.Root
                    collection={bodyTypeCollection}
                    value={[node.type]}
                    onValueChange={(e) => updateBodyNode(node.id, 'type', e.value[0])}
                    w={'130px'}
                    flexShrink={0}
                >
                    <Select.HiddenSelect/>
                    <Select.Trigger>
                        <Select.ValueText placeholder="Type"/>
                    </Select.Trigger>
                    <Select.Positioner>
                        <Select.Content>
                            {bodyTypeCollection.items.map((item) => (
                                <Select.Item key={item.value} item={item}>{item.label}</Select.Item>
                            ))}
                        </Select.Content>
                    </Select.Positioner>
                </Select.Root>
                {!isComplex && (
                    <Input
                        variant={'primary'}
                        placeholder="Default value"
                        value={node.defaultValue}
                        onChange={(e) => updateBodyNode(node.id, 'defaultValue', e.target.value)}
                        flex={1}
                    />
                )}
                <Switch.Root
                    checked={node.required}
                    onCheckedChange={(e) => updateBodyNode(node.id, 'required', e.checked)}
                    colorPalette={'green'}
                    flexShrink={0}
                >
                    <Switch.HiddenInput/>
                    <Switch.Control>
                        <Switch.Thumb>
                            <Switch.ThumbIndicator fallback={<HiX color="black"/>}>
                                <HiCheck/>
                            </Switch.ThumbIndicator>
                        </Switch.Thumb>
                    </Switch.Control>
                    <Switch.Label color={'text.grey'} fontSize={13}>Required</Switch.Label>
                </Switch.Root>
                {(node.type === "object" || (node.type === "array" && node.children.length === 0)) && (
                    <Icon fontSize={20} color={'text.grey'} cursor={'pointer'} onClick={() => addChildBodyNode(node.id)} flexShrink={0}>
                        <HiOutlinePlus/>
                    </Icon>
                )}
                <Icon fontSize={20} color={'text.grey'} cursor={'pointer'} onClick={() => removeBodyNode(node.id)} flexShrink={0}>
                    <HiOutlineTrash/>
                </Icon>
            </HStack>
            {node.children?.map(child => (
                <BodySchemaNode
                    key={child.id}
                    node={child}
                    depth={depth + 1}
                    addChildBodyNode={addChildBodyNode}
                    removeBodyNode={removeBodyNode}
                    updateBodyNode={updateBodyNode}
                    isArrayItem={node.type === "array"}
                />
            ))}
        </VStack>
    )
}

const ConfigureEndpointModal = ({isOpen, onClose, onSave, endpoint, endpoints}) => {
    const [isSaving, setIsSaving] = useState(false)
    const [jsonImportText, setJsonImportText] = useState("")
    const [jsonImportError, setJsonImportError] = useState(false)

    const handleImport = () => {
        const ok = importBodySchemaFromJson(jsonImportText)
        if (ok) {
            setJsonImportText("")
            setJsonImportError(false)
        } else {
            setJsonImportError(true)
        }
    }

    const {
        name, setName,
        tag, setTag,
        description, setDescription,
        method, setMethod,
        url, setUrl,
        providerCombobox,
        providerCollection,
        clearProvider,
        params,
        handleParamChange,
        handleAddParam,
        handleRemoveParam,
        bodySchema,
        addRootBodyNode,
        addChildBodyNode,
        removeBodyNode,
        updateBodyNode,
        importBodySchemaFromJson,
        buildSavePayload,
        NEW_PROVIDER_VALUE,
    } = useEndpointForm({endpoint, endpoints, isOpen})

    const handleSave = async () => {
        setIsSaving(true)
        try {
            await onSave?.(buildSavePayload())
            showToast(endpoint ? "Endpoint updated" : "Endpoint created", null, "success")
            onClose()
        } catch {
            showToast("Error", "Failed to save endpoint", "error")
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} size={'xl'} placement={'end'}>
            <Portal>
                <Drawer.Backdrop/>
                <Drawer.Positioner padding="4">
                    <Drawer.Content minW={'100%'} bg={"bg.drawer"} borderRadius={16}>
                        <Drawer.Header>
                            <HStack justifyContent={'space-between'} w={'full'}>
                                <Text color={'white'}>{endpoint ? 'Edit Endpoint' : 'Configure Endpoint'}</Text>
                                <Icon fontSize={32} color={'text.grey'} onClick={onClose} cursor={'pointer'}>
                                    <IoCloseSharp/>
                                </Icon>
                            </HStack>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack w={{sm: 'full', xl: '50%'}} alignItems={'flex-start'} mx={'auto'}>
                                <Field.Root required>
                                    <Field.Label color={'white'} fontSize={18}>
                                        Endpoint name <Field.RequiredIndicator/>
                                    </Field.Label>
                                    <Input variant={'primary'} placeholder="Enter endpoint name" value={name} onChange={(e) => setName(e.target.value)}/>
                                </Field.Root>

                                <Field.Root mt={4}>
                                    <Field.Label color={'white'} fontSize={18}>Endpoint Tag</Field.Label>
                                    <Input
                                        variant={'primary'}
                                        placeholder="#tagName"
                                        value={tag}
                                        onChange={(e) => {
                                            const val = e.target.value
                                            setTag(val.startsWith('#') || val === '' ? val : '#' + val)
                                        }}
                                    />
                                </Field.Root>

                                <Field.Root mt={4}>
                                    <Field.Label color={'white'} fontSize={18}>Dostawca API</Field.Label>
                                    <Combobox.RootProvider
                                        value={providerCombobox}
                                        w={'full'}
                                        positioning={{strategy: "fixed", hideWhenDetached: true}}
                                    >
                                        <Combobox.Control>
                                            <Combobox.Input placeholder="np. OpenAI, Stripe..."/>
                                            <Combobox.IndicatorGroup>
                                                <Combobox.ClearTrigger color={'gray.600'} onClick={clearProvider}/>
                                                <Combobox.Trigger pr={1}>
                                                    <Icon size={"lg"} color={'white'}><LuChevronDown/></Icon>
                                                </Combobox.Trigger>
                                            </Combobox.IndicatorGroup>
                                        </Combobox.Control>
                                        <Combobox.Positioner>
                                            <Combobox.Content>
                                                <Combobox.Empty color={'gray.500'}>Brak zapisanych dostawców</Combobox.Empty>
                                                {providerCollection.items.map((item) => (
                                                    <Combobox.Item item={item} key={item.value}>
                                                        {item.value === NEW_PROVIDER_VALUE ? (
                                                            <Combobox.ItemText>{`+ Dodaj "${item.label}"`}</Combobox.ItemText>
                                                        ) : (
                                                            <Combobox.ItemText>{item.label}</Combobox.ItemText>
                                                        )}
                                                        <Combobox.ItemIndicator/>
                                                    </Combobox.Item>
                                                ))}
                                            </Combobox.Content>
                                        </Combobox.Positioner>
                                    </Combobox.RootProvider>
                                </Field.Root>

                                <Field.Root mt={4}>
                                    <Field.Label color={'white'} fontSize={18}>Description</Field.Label>
                                    <Textarea
                                        variant={'outline'}
                                        placeholder="Enter endpoint description"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value.slice(0, 100))}
                                        maxLength={100}
                                        resize={'none'}
                                        rows={3}
                                        color={'white'}
                                    />
                                    <Field.HelperText>{description.length}/100</Field.HelperText>
                                </Field.Root>

                                <Separator variant="solid" w={'full'} borderColor={'gray.700'} mt={4} mb={2}/>

                                <Field.Root required>
                                    <Field.Label color={'white'} fontSize={18}>
                                        Method & URL <Field.RequiredIndicator/>
                                    </Field.Label>
                                    <HStack w={'full'}>
                                        <Select.Root
                                            collection={methodCollection}
                                            value={method}
                                            onValueChange={(e) => setMethod(e.value)}
                                            w={'140px'}
                                            flexShrink={0}
                                        >
                                            <Select.HiddenSelect/>
                                            <Select.Trigger>
                                                <Select.ValueText placeholder="Method"/>
                                            </Select.Trigger>
                                            <Select.Positioner>
                                                <Select.Content>
                                                    {methodCollection.items.map((item) => (
                                                        <Select.Item key={item.value} item={item}>
                                                            {item.label}
                                                        </Select.Item>
                                                    ))}
                                                </Select.Content>
                                            </Select.Positioner>
                                        </Select.Root>
                                        <Input variant={'primary'} placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} flex={1}/>
                                    </HStack>
                                </Field.Root>

                                <Separator variant="solid" w={'full'} borderColor={'gray.700'} mt={4} mb={2}/>

                                <Field.Root>
                                    <Field.Label color={'white'} fontSize={18}>Parameters</Field.Label>
                                    <Field.HelperText>Define the parameters accepted by this endpoint.</Field.HelperText>
                                </Field.Root>

                                <VStack w={'full'} gap={3} mt={2}>
                                    {params.map((param) => (
                                        <HStack key={param.id} w={'full'} gap={2} alignItems={'center'}>
                                            <Input
                                                variant={'primary'}
                                                placeholder="Name"
                                                value={param.name}
                                                onChange={(e) => handleParamChange(param.id, 'name', e.target.value)}
                                                flex={1}
                                            />
                                            <Input
                                                variant={'primary'}
                                                placeholder="Type"
                                                value={param.type}
                                                onChange={(e) => handleParamChange(param.id, 'type', e.target.value)}
                                                flex={1}
                                            />
                                            <Input
                                                variant={'primary'}
                                                placeholder="Default value"
                                                value={param.defaultValue}
                                                onChange={(e) => handleParamChange(param.id, 'defaultValue', e.target.value)}
                                                flex={1}
                                            />
                                            <Switch.Root
                                                checked={param.required}
                                                onCheckedChange={(e) => handleParamChange(param.id, 'required', e.checked)}
                                                colorPalette={'green'}
                                                flexShrink={0}
                                            >
                                                <Switch.HiddenInput/>
                                                <Switch.Control>
                                                    <Switch.Thumb>
                                                        <Switch.ThumbIndicator fallback={<HiX color="black"/>}>
                                                            <HiCheck/>
                                                        </Switch.ThumbIndicator>
                                                    </Switch.Thumb>
                                                </Switch.Control>
                                                <Switch.Label color={'text.grey'} fontSize={13}>Required</Switch.Label>
                                            </Switch.Root>
                                            {params.length > 1 && (
                                                <Icon
                                                    fontSize={20}
                                                    color={'text.grey'}
                                                    cursor={'pointer'}
                                                    onClick={() => handleRemoveParam(param.id)}
                                                    flexShrink={0}
                                                >
                                                    <HiOutlineTrash/>
                                                </Icon>
                                            )}
                                        </HStack>
                                    ))}
                                </VStack>

                                <Button variant="outlined" textDecoration='unset' onClick={handleAddParam} mt={2}>
                                    <Icon fontSize={46}><HiOutlinePlus/></Icon>
                                    <Text>Add parameter</Text>
                                </Button>

                                <Separator variant="solid" w={'full'} borderColor={'gray.700'} mt={4} mb={2}/>

                                <Field.Root>
                                    <Field.Label color={'white'} fontSize={18}>Body Schema</Field.Label>
                                    <Field.HelperText>Define the nested structure of the request body.</Field.HelperText>
                                </Field.Root>

                                <VStack w={'full'} gap={2} alignItems={'flex-start'} mt={2}>
                                    <Textarea
                                        placeholder='Paste sample JSON here to auto-generate schema…'
                                        value={jsonImportText}
                                        onChange={(e) => { setJsonImportText(e.target.value); setJsonImportError(false) }}
                                        size={'sm'}
                                        rows={4}
                                        borderColor={jsonImportError ? 'red.400' : undefined}
                                        color={'white'}
                                        resize={'none'}
                                    />
                                    {jsonImportError && <Text fontSize={12} color={'red.400'}>Invalid JSON or not a plain object</Text>}
                                    <Button size={'sm'} variant={'outline'} onClick={handleImport} disabled={!jsonImportText.trim()}>
                                        Import
                                    </Button>
                                </VStack>

                                <VStack w={'full'} gap={3} mt={2}>
                                    {bodySchema.map(node => (
                                        <BodySchemaNode
                                            key={node.id}
                                            node={node}
                                            depth={0}
                                            addChildBodyNode={addChildBodyNode}
                                            removeBodyNode={removeBodyNode}
                                            updateBodyNode={updateBodyNode}
                                        />
                                    ))}
                                </VStack>

                                <Button variant="outlined" textDecoration='unset' onClick={addRootBodyNode} mt={2}>
                                    <Icon fontSize={46}><HiOutlinePlus/></Icon>
                                    <Text>Add field</Text>
                                </Button>
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer borderRadius={16}>
                            <HStack justifyContent={'flex-end'} w={'full'} gap={3}>
                                <Button variant="outlined" textDecoration='unset' onClick={onClose} disabled={isSaving}>Cancel</Button>
                                <Button variant="primary" textDecoration='unset' onClick={handleSave} disabled={isSaving}>
                                    {isSaving ? <Spinner size="sm"/> : "Save"}
                                </Button>
                            </HStack>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    )
}

export default ConfigureEndpointModal;
